import { Wrench } from '@/components/icons'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn } from '@/lib/utils'
import type { MaintenanceAircraft } from '../lib/hooks'

interface MaintenanceIndicatorProps {
  aircraft: MaintenanceAircraft[]
  iconClassName?: string
}

/**
 * Wrench icon for maintenance days. Tapping it opens a small card listing the
 * aircraft in maintenance for that day. Renders nothing when the list is empty.
 *
 * The trigger is a <span role="button"> (not a <button>) with stopPropagation so
 * it can live inside another button (e.g. the calendar day cell) without nesting
 * interactive elements or triggering the parent's click handler.
 */
export function MaintenanceIndicator({ aircraft, iconClassName }: MaintenanceIndicatorProps) {
  if (aircraft.length === 0) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center cursor-pointer"
          aria-label="Aircraft in maintenance"
        >
          <Wrench className={cn('size-4 text-background-notice-bold shrink-0', iconClassName)} />
        </span>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        className="w-auto max-w-[240px] p-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-semibold text-content-primary-subtle mb-2">In maintenance</p>
        <ul className="flex flex-col gap-2">
          {aircraft.map((a) => (
            <li key={a.id} className="flex items-center gap-2">
              <Wrench className="size-4 text-background-notice-bold shrink-0" />
              <span className="flex flex-col">
                <span className="text-sm font-medium text-content-primary-bold leading-tight">
                  {a.registration}
                </span>
                <span className="text-xs text-content-primary-subtle leading-tight">
                  Until {a.end.format('MMM D, YYYY')}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
